import { formatUsd, parseGaju, parseUsd } from '@xchange/core';
import { decimalSeparator } from './numbers.js';

/** The three tokens, each its own currency, each shown its own way. */
export type Asset = 'GAJU' | 'USDC' | 'XGE';

export const ASSETS: Asset[] = ['GAJU', 'USDC', 'XGE'];

/**
 * Gaju as people read it: two decimals from one up ("1,000.00"), up to six
 * below one ("0.002"), and "<0.000001" for dust rather than eighteen digits.
 */
export function gajuText(units: bigint): string {
  const neg = units < 0n;
  const abs = neg ? -units : units;
  const whole = abs / 10n ** 18n;
  const sign = neg ? '-' : '';
  // Below one Gaju: up to six decimals, so 0.002 still shows; dust is named.
  if (whole === 0n && abs > 0n) {
    const frac = (abs / 10n ** 12n).toString().padStart(6, '0').replace(/0+$/, '');
    return frac === '' ? `${sign}<0.000001` : `${sign}0.${frac}`;
  }
  // One Gaju and up, and zero: always two decimals, floored.
  const cents = ((abs % 10n ** 18n) / 10n ** 16n).toString().padStart(2, '0');
  return `${sign}${whole.toLocaleString('en-US')}.${cents}`;
}

/** "1,000.00 Gaju", "$21.15", "12.50 XGE". */
export function amountText(asset: Asset, units: bigint): string {
  if (asset === 'GAJU') return `${gajuText(units)} Gaju`;
  // Dollars always show two decimals, floored.
  if (asset === 'USDC') return formatUsd(units);
  // XGE like Gaju: two decimals from one up, up to six below one.
  const neg = units < 0n;
  const abs = neg ? -units : units;
  const whole = abs / 1_000_000n;
  const sign = neg ? '-' : '';
  if (whole === 0n && abs > 0n)
    return `${sign}0.${abs.toString().padStart(6, '0').replace(/0+$/, '')} XGE`;
  const cents = ((abs % 1_000_000n) / 10_000n).toString().padStart(2, '0');
  return `${sign}${whole.toLocaleString('en-US')}.${cents} XGE`;
}

/** XGE from minor units, without the name: "2,000", "12.5". */
export function xgeText(units: string | bigint): string {
  return amountText('XGE', BigInt(units)).replace(' XGE', '');
}

/** The bare number, for a balance tile that already names the token. */
export function bareText(asset: Asset, units: bigint): string {
  return asset === 'GAJU' ? gajuText(units) : amountText(asset, units).replace(' XGE', '');
}

export function parseAmount(asset: Asset, text: string): bigint {
  if (asset === 'GAJU') return parseGaju(text);
  // USDC and XGE both have six decimals; the dollar sign is optional either way.
  return parseUsd(text.replace('$', ''));
}

export const assetName: Record<Asset, string> = { GAJU: 'Gaju', USDC: 'USDC', XGE: 'XGE' };
export const chainName: Record<Asset, string> = { GAJU: 'Groot', USDC: 'Solana', XGE: 'Solana' };
/**
 * The asset as named where someone is about to send it in. USDC exists on many
 * networks and only Solana's arrives here, so it alone carries its network.
 */
export const onChain: Record<Asset, string> = { GAJU: 'Gaju', USDC: 'USDC (Solana)', XGE: 'XGE' };

const DECIMALS_OF: Record<Asset, number> = { GAJU: 18, USDC: 6, XGE: 6 };

/**
 * An exact amount as someone would type it into an amount box: every digit,
 * no grouping, never rounded, in the person's own decimal separator so the
 * box reads it back as the same number ("1999.123456", or "1999,123456").
 */
export function inputText(asset: Asset, units: bigint): string {
  const d = DECIMALS_OF[asset];
  const scale = 10n ** BigInt(d);
  const whole = (units / scale).toString();
  const frac = (units % scale).toString().padStart(d, '0').replace(/0+$/, '');
  return frac === '' ? whole : `${whole}${decimalSeparator()}${frac}`;
}
